import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import ExpandableImageComponent from '../../Image/ExpandableImageComponent';

import { avatarPlaceholderSRC } from '../../../utils';

const StyledContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '10rem',
  backgroundColor: theme.palette.common.white,
  border: `.1rem solid ${theme.palette.grey[300]}`,
  borderBottom: 0,

  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.palette.grey[100],
  },
  '&:last-child': {
    borderBottom: `.1rem solid ${theme.palette.grey[300]}`,
  },
}));

const StyledTextWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  margin: '1.5rem',
  marginLeft: 0,
  width: `calc(100% - 11rem)`,
}));

const StyledTitleContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontWeight: 'inherit',
  gap: '1rem',
}));

const StyledRecipientInfo = styled(Box)(() => ({
  alignSelf: 'start',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

const StyledRecipientInfoText = styled('span')(() => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
}));

const MessagesTemplateListItemComponent = ({
  threadId,
  photo,
  recipientInfo,
  createdAt,
  messagePreview,
  setCurrentChat,
  isCurrentChat,
  isHasUpdatesShown,
  postSubject,
  isClosed,
  dateFormatter,
}) => {
  const { t } = useTransl();

  return (
    <StyledContainer
      key={threadId}
      building={threadId}
      onClick={() => setCurrentChat(threadId)}
      sx={(theme) =>
        isCurrentChat ? { backgroundColor: theme.palette.custom.tab } : {}
      }
    >
      <Box sx={[{ display: 'flex' }, isClosed && { opacity: '40%' }]}>
        <Box sx={{ margin: '1.5rem' }}>
          <ExpandableImageComponent
            src={avatarPlaceholderSRC}
            photo={photo}
            caption={recipientInfo}
            height="6rem"
            isSquare
          />
        </Box>
        <StyledTextWrapper
          sx={(theme) =>
            isHasUpdatesShown
              ? {
                  fontWeight: theme.typography.fontWeightBold,
                }
              : {}
          }
        >
          <StyledTitleContainer>
            <StyledRecipientInfo>
              <StyledRecipientInfoText>{recipientInfo}</StyledRecipientInfoText>
            </StyledRecipientInfo>
            <Box sx={{ whiteSpace: 'nowrap' }}>{dateFormatter(createdAt)}</Box>
          </StyledTitleContainer>
          {postSubject && (
            <div className="crop-long-text">
              {t('messages.postSubject', {
                postSubject,
              })}
            </div>
          )}
          <div className="crop-long-text">{messagePreview}</div>
        </StyledTextWrapper>
      </Box>
    </StyledContainer>
  );
};

MessagesTemplateListItemComponent.defaultProps = {
  photo: null,
  createdAt: null,
  postSubject: '',
  isClosed: false,
};

MessagesTemplateListItemComponent.propTypes = {
  threadId: PropTypes.number.isRequired,
  photo: PropTypes.shape({
    thumbnailUrl: PropTypes.string,
    url: PropTypes.string,
  }),
  recipientInfo: PropTypes.string.isRequired,
  createdAt: PropTypes.string,
  messagePreview: PropTypes.string.isRequired,
  setCurrentChat: PropTypes.func.isRequired,
  isCurrentChat: PropTypes.bool.isRequired,
  isHasUpdatesShown: PropTypes.bool.isRequired,
  dateFormatter: PropTypes.func.isRequired,
  postSubject: PropTypes.string,
  isClosed: PropTypes.bool,
};

export default MessagesTemplateListItemComponent;
